import { useCallback } from 'react'
import { LOCALES, currentLocale, type Locale } from './index'
import { useLocale } from '@/components/providers/locale-provider'

/** Every top-level page, addressed by id rather than by a hardcoded path. */
export const ROUTE_IDS = ['termBase', 'preview', 'jobs', 'prompts', 'settings'] as const
export type RouteId = (typeof ROUTE_IDS)[number]

/**
 * The path segment each page uses per locale.
 *
 * Every locale's segments stay registered in the router at once, so a link
 * someone shared from the Estonian UI still opens for a colleague reading in
 * Finnish — it just rewrites itself to the reader's own segment on arrival.
 *
 * Query-parameter *names* (`kogu`, `too`, `termin`, …) are deliberately not
 * translated: they are the shape of a shared link, and rewriting them per
 * locale would break every URL already pasted into a document or an email.
 */
export const ROUTE_SEGMENTS: Record<Locale, Record<RouteId, string>> = {
  et: {
    termBase: 'terminibaas',
    preview: 'eelvaade',
    jobs: 'tood',
    prompts: 'promptid',
    settings: 'seaded',
  },
  en: {
    termBase: 'term-base',
    preview: 'preview',
    jobs: 'jobs',
    prompts: 'prompts',
    settings: 'settings',
  },
  fi: {
    termBase: 'termipankki',
    preview: 'esikatselu',
    jobs: 'tyot',
    prompts: 'kehotteet',
    settings: 'asetukset',
  },
}

/** Build a path for `id` in `locale`, e.g. ('preview', 'et', 'abc') -> /eelvaade/abc */
export function pathFor(id: RouteId, locale: Locale = currentLocale(), suffix = ''): string {
  const base = `/${ROUTE_SEGMENTS[locale][id]}`
  return suffix ? `${base}/${suffix}` : base
}

/** Which page (and which locale's spelling) a pathname refers to, if any. */
export function matchRoute(pathname: string): { id: RouteId; locale: Locale; rest: string } | null {
  const [, segment = '', ...rest] = pathname.split('/')
  for (const locale of LOCALES) {
    for (const id of ROUTE_IDS) {
      if (ROUTE_SEGMENTS[locale][id] === segment) {
        return { id, locale, rest: rest.join('/') }
      }
    }
  }
  return null
}

/** `path('jobs')` -> the current locale's path for the jobs page. */
export function useLocalizedPath() {
  const { locale } = useLocale()
  return useCallback((id: RouteId, suffix = '') => pathFor(id, locale, suffix), [locale])
}
